/**
 * Presentational rules for the document-import card (ImportBatchCard): the
 * draft's `import` object (spec §7.4, frozen as draft_import_v1.json) turned
 * into columns, row chips, counts, filters, pages and selection maths. Pure —
 * the card owns state; this module only computes.
 *
 * Selection is client-side only (spec §7.3): `/pending` resets it to the
 * draft's default, and the approve body carries the selectable rows the user
 * UNticked (`excluded_rows`), so an untouched card approves with no field.
 */
import { createRoute, PATHS } from '@src/core/router/paths';
import { fieldCaption, type Language } from './card-labels';
import {
  importLabel,
  sameAsRowLabel,
  type ImportProblem,
} from './import-labels';

export {
  problemSentence,
  formatPageList,
  type ImportProblem,
} from './import-labels';

export type RowStatus =
  | 'ready'
  | 'warning'
  | 'duplicate_existing'
  | 'duplicate_in_file'
  | 'invalid'
  | 'over_plan_limit';

export interface DuplicateOf {
  record_type: string;
  /**
   * null (with label '') when the match is another partner's record, hidden
   * on purpose: the chip then shows no link and no name (fixture c62fd3a2).
   */
  id: number | string | null;
  label: string;
  match?: string;
}

export interface ImportRow {
  row: number;
  page?: number | null;
  values: Record<string, unknown>;
  status: RowStatus;
  selectable: boolean;
  selected?: boolean;
  problems: ImportProblem[];
  duplicate_of?: DuplicateOf | null;
  duplicate_of_row?: number | null;
}

export interface ImportBatch {
  batch_id?: number;
  record_type: string;
  source?: string;
  columns: string[];
  defaults: Array<{ field: string; label: string }>;
  instructions: string;
  counts?: Record<string, number>;
  plan: { feature?: string; remaining: number } | null;
  truncated: { rows_found: number; max_rows: number } | null;
  unreadable_pages: number[];
  invitations: { emails_per_selected_row: number } | null;
  rows: ImportRow[];
}

export const IMPORT_PAGE_SIZE = 25;

const STATUSES: ReadonlySet<string> = new Set([
  'ready',
  'warning',
  'duplicate_existing',
  'duplicate_in_file',
  'invalid',
  'over_plan_limit',
]);

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readRow(value: unknown): ImportRow | null {
  if (!isObject(value)) return null;
  if (typeof value.row !== 'number') return null;
  const status = typeof value.status === 'string' ? value.status : '';
  if (!STATUSES.has(status)) return null;
  const problems = Array.isArray(value.problems)
    ? value.problems.filter(
        (p): p is ImportProblem => isObject(p) && typeof p.code === 'string'
      )
    : [];
  const duplicateOf = isObject(value.duplicate_of)
    ? (value.duplicate_of as unknown as DuplicateOf)
    : null;
  return {
    row: value.row,
    page: typeof value.page === 'number' ? value.page : null,
    values: isObject(value.values) ? value.values : {},
    status: status as RowStatus,
    // Only ready/warning rows can ever be imported, whatever the flag says.
    selectable:
      value.selectable === true && (status === 'ready' || status === 'warning'),
    selected: value.selected === true,
    problems,
    duplicate_of: duplicateOf,
    duplicate_of_row:
      typeof value.duplicate_of_row === 'number'
        ? value.duplicate_of_row
        : null,
  };
}

/**
 * The draft's `import` object, or null when the draft is not an import (the
 * card then renders the ordinary key/value confirmation).
 */
export function readImportBatch(value: unknown): ImportBatch | null {
  if (!isObject(value)) return null;
  if (typeof value.record_type !== 'string') return null;
  if (!Array.isArray(value.rows) || !Array.isArray(value.columns)) return null;
  const rows = value.rows
    .map(readRow)
    .filter((row): row is ImportRow => row !== null);
  const plan =
    isObject(value.plan) && typeof value.plan.remaining === 'number'
      ? (value.plan as ImportBatch['plan'])
      : null;
  const truncated =
    isObject(value.truncated) &&
    typeof value.truncated.rows_found === 'number' &&
    typeof value.truncated.max_rows === 'number'
      ? (value.truncated as ImportBatch['truncated'])
      : null;
  const invitations =
    isObject(value.invitations) &&
    typeof value.invitations.emails_per_selected_row === 'number'
      ? (value.invitations as ImportBatch['invitations'])
      : null;
  return {
    batch_id: typeof value.batch_id === 'number' ? value.batch_id : undefined,
    record_type: value.record_type,
    source: typeof value.source === 'string' ? value.source : undefined,
    columns: value.columns.filter((c): c is string => typeof c === 'string'),
    defaults: Array.isArray(value.defaults)
      ? value.defaults.filter(
          (d): d is { field: string; label: string } =>
            isObject(d) &&
            typeof d.field === 'string' &&
            typeof d.label === 'string'
        )
      : [],
    instructions:
      typeof value.instructions === 'string' ? value.instructions.trim() : '',
    plan,
    truncated,
    unreadable_pages: Array.isArray(value.unreadable_pages)
      ? value.unreadable_pages.filter((p): p is number => typeof p === 'number')
      : [],
    invitations,
    rows,
  };
}

export interface ImportColumn {
  key: string;
  caption: string;
}

/** The record type's columns, captioned in the card language. */
export function importColumns(
  batch: ImportBatch,
  language: Language
): ImportColumn[] {
  return batch.columns.map((key) => ({
    key,
    caption: fieldCaption(language, key),
  }));
}

/** The sticky name column: `name`, else `first_name`, else the first one. */
export function nameColumn(batch: ImportBatch): string | undefined {
  if (batch.columns.includes('name')) return 'name';
  if (batch.columns.includes('first_name')) return 'first_name';
  return batch.columns[0];
}

export type ChipTone = 'neutral' | 'amber' | 'grey' | 'red' | 'plan';

export interface RowChip {
  tone: ChipTone;
  label: string;
}

/** A row's status chip. */
export function rowChip(row: ImportRow, language: Language): RowChip {
  switch (row.status) {
    case 'ready':
      return { tone: 'neutral', label: importLabel(language, 'chipReady') };
    case 'warning':
      return { tone: 'amber', label: importLabel(language, 'chipWarning') };
    case 'duplicate_existing':
      return {
        tone: 'grey',
        label: importLabel(language, 'chipDuplicateExisting'),
      };
    case 'duplicate_in_file': {
      const earlier =
        row.duplicate_of_row ??
        row.problems.find((p) => p.code === 'duplicate_in_file')?.detail;
      return {
        tone: 'grey',
        label: earlier
          ? sameAsRowLabel(language, earlier)
          : importLabel(language, 'chipDuplicateExisting'),
      };
    }
    case 'invalid':
      return { tone: 'red', label: importLabel(language, 'chipInvalid') };
    case 'over_plan_limit':
      return {
        tone: 'plan',
        label: importLabel(language, 'chipOverPlanLimit'),
      };
  }
}

// Severity per problem code (collab/import/problem-codes.md): warnings leave
// the row ticked; the duplicate/plan codes mirror a grey status; every other
// binding code makes the row invalid.
const WARNING_CODES: ReadonlySet<string> = new Set([
  'phone_region_assumed',
  'phone_country_inferred',
  'name_only_duplicate',
  'not_in_document',
  // The value is dropped and the row still imports (846baca6).
  'field_not_allowed',
  // This row and its neighbour may be one record split across a page break.
  'possible_split_row',
  // The student already exists in the CRM; importing enrols them in this
  // program and sends them an invitation (user decision 2026-10-13).
  'existing_student_enrolled',
  // An optional choice value matched no code, label or known synonym; the
  // value is dropped (field left empty) and the row still imports.
  'unrecognised_choice',
]);
const GREY_CODES: ReadonlySet<string> = new Set([
  'duplicate_existing',
  'duplicate_in_file',
  'over_plan_limit',
]);
const ERROR_CODES: ReadonlySet<string> = new Set([
  'required',
  'email_or_phone_required',
  'invalid',
  'invalid_date',
  'invalid_number',
  'invalid_choice',
  'invalid_phone',
  'invalid_email',
  'phone_region_unknown',
  'email_in_use',
]);

const TONE_RANK: Record<ChipTone, number> = {
  neutral: 0,
  grey: 1,
  plan: 1,
  amber: 2,
  red: 3,
};

/**
 * One problem's own severity, for outlining the cell it names. An unknown
 * code follows the row's status (red on an invalid row, amber otherwise).
 */
export function problemTone(problem: ImportProblem, row: ImportRow): ChipTone {
  if (WARNING_CODES.has(problem.code)) return 'amber';
  if (GREY_CODES.has(problem.code)) return 'grey';
  if (ERROR_CODES.has(problem.code)) return 'red';
  return row.status === 'invalid' ? 'red' : 'amber';
}

/** The phone column named by the pseudo-field "email_or_phone". */
function emailOrPhonePhoneField(recordType: string): string {
  return recordType === 'contact' ? 'phone' : 'phone_number';
}

/**
 * Each named field's outline tone: the most severe problem on that field.
 * The pseudo-field "email_or_phone" (a `required` problem on a lead/contact
 * with neither) names no column of its own, so it expands to the record
 * type's actual email and phone columns — only the ones present on the card.
 */
export function fieldTones(
  row: ImportRow,
  recordType: string,
  columns: readonly string[]
): Map<string, ChipTone> {
  const tones = new Map<string, ChipTone>();
  const present = new Set(columns);
  const set = (field: string, tone: ChipTone) => {
    const previous = tones.get(field);
    if (!previous || TONE_RANK[tone] > TONE_RANK[previous]) {
      tones.set(field, tone);
    }
  };
  for (const problem of row.problems) {
    if (typeof problem.field !== 'string') continue;
    const tone = problemTone(problem, row);
    if (problem.field === 'email_or_phone') {
      if (present.has('email')) set('email', tone);
      const phoneField = emailOrPhonePhoneField(recordType);
      if (present.has(phoneField)) set(phoneField, tone);
      continue;
    }
    set(problem.field, tone);
  }
  return tones;
}

/** A row the card highlights: anything but a clean ready row. */
export function hasProblems(row: ImportRow): boolean {
  return row.status !== 'ready' || row.problems.length > 0;
}

export interface ImportCounts {
  ready: number;
  warning: number;
  duplicates: number;
  invalid: number;
  over_plan_limit: number;
}

/** The count strip, counted from the rows on the card. */
export function importCounts(batch: ImportBatch): ImportCounts {
  const counts: ImportCounts = {
    ready: 0,
    warning: 0,
    duplicates: 0,
    invalid: 0,
    over_plan_limit: 0,
  };
  for (const row of batch.rows) {
    if (
      row.status === 'duplicate_existing' ||
      row.status === 'duplicate_in_file'
    ) {
      counts.duplicates += 1;
    } else {
      counts[row.status] += 1;
    }
  }
  return counts;
}

export type ImportFilter = 'all' | keyof ImportCounts;

function inGroup(row: ImportRow, filter: ImportFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'duplicates')
    return (
      row.status === 'duplicate_existing' || row.status === 'duplicate_in_file'
    );
  return row.status === filter;
}

/** The rows a count-strip filter and "Show problems only" leave visible. */
export function filterRows(
  rows: readonly ImportRow[],
  filter: ImportFilter,
  problemsOnly: boolean
): ImportRow[] {
  return rows.filter(
    (row) => inGroup(row, filter) && (!problemsOnly || hasProblems(row))
  );
}

/** One page of rows (0-based `page`, clamped to the last page). */
export function paginate<T>(
  rows: readonly T[],
  page: number,
  pageSize: number = IMPORT_PAGE_SIZE
): { rows: T[]; page: number; pageCount: number } {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(Math.max(0, page), pageCount - 1);
  return {
    rows: rows.slice(current * pageSize, (current + 1) * pageSize),
    page: current,
    pageCount,
  };
}

/** The rows ticked when the card opens (the server's default). */
export function defaultSelection(batch: ImportBatch): Set<number> {
  return new Set(
    batch.rows.filter((r) => r.selectable && r.selected).map((r) => r.row)
  );
}

/** Every importable row: "Select all ready". */
export function selectableRows(batch: ImportBatch): number[] {
  return batch.rows.filter((r) => r.selectable).map((r) => r.row);
}

/** The approve body's excluded_rows: selectable rows the user unticked. */
export function excludedRows(
  batch: ImportBatch,
  selection: ReadonlySet<number>
): number[] {
  return batch.rows
    .filter((r) => r.selectable && !selection.has(r.row))
    .map((r) => r.row);
}

/** How many selected rows will be imported. */
export function selectedCount(
  batch: ImportBatch,
  selection: ReadonlySet<number>
): number {
  return batch.rows.filter((r) => r.selectable && selection.has(r.row)).length;
}

/** Student imports: emails sent on approve; null when nothing is sent. */
export function invitationCount(
  batch: ImportBatch,
  selection: ReadonlySet<number>
): number | null {
  if (!batch.invitations) return null;
  return (
    selectedCount(batch, selection) * batch.invitations.emails_per_selected_row
  );
}

/**
 * The existing record's detail page, where the app has one mounted:
 * lead → /leads/:id, contact → /contacts/:id (the inbox contact book),
 * task → /tasks?id=:id (the task list opens its details dialog from the
 * URL). Products and students have no detail route, so they get none.
 */
export function duplicateRoute(duplicate: DuplicateOf): string | null {
  const { id } = duplicate;
  if (id === undefined || id === null || id === '') return null;
  switch (duplicate.record_type) {
    case 'lead':
      return createRoute(`/leads/${id}`);
    case 'contact':
      return createRoute(PATHS.CONTACT_DETAIL(id));
    case 'task':
      return createRoute(PATHS.TASKS, null, { id });
    default:
      return null;
  }
}

function text(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
}

/** The row's person/record name for its checkbox label. */
export function rowName(row: ImportRow): string {
  const name = text(row.values.name);
  if (name) return name;
  return [text(row.values.first_name), text(row.values.last_name)]
    .filter(Boolean)
    .join(' ');
}

/** A cell's display text; empty for a missing or blank value. */
export function cellText(value: unknown): string {
  if (typeof value === 'boolean') return value ? '✓' : '';
  return text(value);
}
